
import { createContext, useContext, useState, ReactNode, useCallback, useRef } from 'react';
import { ReauthModal } from '../components/core/ReauthModal';

interface SudoContextType {
    requestSudo: () => Promise<boolean>;
    isSudoActive: boolean;
}

const SudoContext = createContext<SudoContextType | undefined>(undefined);

const SUDO_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

export const SudoProvider = ({ children }: { children: ReactNode }) => {
    const [lastSudoTime, setLastSudoTime] = useState<number>(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const pendingRequestRef = useRef<{
        promise: Promise<boolean>;
        resolve: (value: boolean) => void;
    } | null>(null);

    const requestSudo = useCallback((): Promise<boolean> => {
        const now = Date.now();
        if (now - lastSudoTime < SUDO_TIMEOUT_MS) {
            return Promise.resolve(true);
        }

        // De-duplicate concurrent sudo prompts so multiple callers share one result.
        if (pendingRequestRef.current) {
            return pendingRequestRef.current.promise;
        }

        let resolveFn!: (value: boolean) => void;
        const promise = new Promise<boolean>((resolve) => {
            resolveFn = resolve;
        });

        pendingRequestRef.current = { promise, resolve: resolveFn };
        setIsModalOpen(true);
        return promise;
    }, [lastSudoTime]);

    const handleModalClose = (success: boolean) => {
        setIsModalOpen(false);
        if (success) {
            setLastSudoTime(Date.now());
        }

        if (pendingRequestRef.current) {
            pendingRequestRef.current.resolve(success);
            pendingRequestRef.current = null;
        }
    };

    const isSudoActive = Date.now() - lastSudoTime < SUDO_TIMEOUT_MS;

    return (
        <SudoContext.Provider value={{ requestSudo, isSudoActive }}>
            {children}
            <ReauthModal open={isModalOpen} onClose={handleModalClose} />
        </SudoContext.Provider>
    );
};

export const useSudo = () => {
    const context = useContext(SudoContext);
    if (context === undefined) {
        throw new Error('useSudo must be used within a SudoProvider');
    }
    return context;
};
